import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Apple, ArrowLeft, Download as DownloadIcon, Globe, Smartphone } from 'lucide-react';

/**
 * Public download page. Reachable with no lab code and no account, because it
 * is the link people are handed before they have either.
 */

/** Set once the App Store listing is live. */
const APP_STORE_URL = '';
const APK_URL = '/blisxbench.apk';

type Platform = 'ios' | 'android' | 'desktop';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  // iPadOS reports itself as a Mac, so touch support is what separates them.
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

function Card({
  icon,
  title,
  children,
  highlight,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <section
      className={
        highlight
          ? 'rounded-xl border border-accent-500/60 bg-accent-500/5 p-5'
          : 'rounded-xl border border-ink-700 bg-ink-900 p-5'
      }
    >
      <div className="mb-3 flex items-center gap-2.5">
        <span className="text-accent-400">{icon}</span>
        <h2 className="text-base font-semibold">{title}</h2>
        {highlight && (
          <span className="ml-auto rounded-md bg-accent-500 px-2 py-0.5 text-xs font-semibold text-ink-950">
            Your device
          </span>
        )}
      </div>
      <div className="space-y-3 text-sm text-ink-300">{children}</div>
    </section>
  );
}

export default function DownloadPage() {
  const [platform, setPlatform] = useState<Platform>('desktop');
  useEffect(() => setPlatform(detectPlatform()), []);

  return (
    <div className="min-h-screen bg-ink-950">
      <header className="border-b border-ink-800">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-5 py-4">
          <Link to="/" className="text-lg font-semibold tracking-tight">
            Blisx<span className="text-accent-400">Bench</span>
          </Link>
          <Link
            to="/"
            className="ml-auto inline-flex items-center gap-1 text-sm text-ink-400 hover:text-ink-100"
          >
            <ArrowLeft size={14} />
            Open the app
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-5 py-10">
        <h1 className="text-2xl font-semibold">Get BlisxBench</h1>
        <p className="mt-2 text-[15px] leading-relaxed text-ink-400">
          Capture lab measurements by photograph, voice or keyboard and export a clean workbook.
          You will need the eight-digit access code from your lab administrator.
        </p>

        <div className="mt-8 space-y-4">
          <Card
            icon={<Smartphone size={18} />}
            title="Android"
            highlight={platform === 'android'}
          >
            <a
              href={APK_URL}
              download="blisxbench.apk"
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-accent-500 px-5 py-3 text-base font-medium text-white transition-colors hover:bg-accent-400"
            >
              <DownloadIcon size={18} />
              Download the APK
            </a>
            <p className="text-ink-400">
              Android will warn that the file came from outside the Play Store. Tap{' '}
              <strong className="text-ink-300">Download anyway</strong>, open it, and allow
              installing from this source when asked.
            </p>
          </Card>

          <Card icon={<Apple size={18} />} title="iPhone" highlight={platform === 'ios'}>
            {APP_STORE_URL ? (
              <a
                href={APP_STORE_URL}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-base font-medium text-ink-950 transition-colors hover:bg-ink-100"
              >
                <Apple size={18} />
                Get it on the App Store
              </a>
            ) : (
              <div className="rounded-lg border border-ink-700 bg-ink-800/60 p-4 text-center">
                <p className="font-medium text-ink-200">Not ready yet</p>
                <p className="mt-1 text-ink-400">Awaiting App Store approval.</p>
              </div>
            )}
          </Card>

          <Card icon={<Globe size={18} />} title="Computer" highlight={platform === 'desktop'}>
            <p>
              No install needed. Open{' '}
              <Link to="/" className="text-accent-400 hover:underline">
                blisxbench.web.app
              </Link>{' '}
              in any browser.
            </p>
            <p className="text-ink-400">
              In Chrome or Edge you can also press the install icon in the address bar to run it in
              its own window.
            </p>
          </Card>
        </div>

        <section className="mt-8 rounded-xl border border-ink-800 p-5">
          <h2 className="mb-2 text-sm font-semibold">Which should I use?</h2>
          <p className="text-sm leading-relaxed text-ink-400">
            They are the same app and share the same data — sign in on any of them and your projects
            are there. The phone builds exist because photographing an instrument screen is a
            one-handed job at the bench. Everything syncs, and capture keeps working with no signal.
          </p>
        </section>

        <footer className="mt-12 border-t border-ink-800 pt-6 text-sm text-ink-400">
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy" className="hover:text-ink-100">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-ink-100">
              Terms
            </Link>
            <Link to="/support" className="hover:text-ink-100">
              Support
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
